import React from "react"
import { Table } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import type { ColumnsType } from "antd/es/table"
import getColumns from "./columns"
import {
  useDeleteDocumentItemMutation,
  useDownloadDocumentMutation,
} from "features/Documents/api/useDocumentsMutations"
import type { Project } from "stores/projectStore"
import type { DocumentItem } from "types/document.types"

// Define the props interface
interface DocumentTableProps {
  documents: DocumentItem[]
  isLoading: boolean
  project: Project
  onFolderClick: (folderName: string) => void
}

/**
 * Component for displaying a table of documents with folder support.
 * The document list refreshes via the delete mutation's cache invalidation.
 *
 * @param props - The component props.
 * @returns {JSX.Element} The rendered table component.
 */
const DocumentTable: React.FC<DocumentTableProps> = ({
  project,
  documents,
  isLoading,
  onFolderClick,
}) => {
  const deleteItemMutation = useDeleteDocumentItemMutation()
  const downloadMutation = useDownloadDocumentMutation()

  /**
   * Deletes a document or folder from the project.
   *
   * @param fullPath - The full path of the file or folder to delete.
   * @param isFolder - Whether the item is a folder.
   */
  const deleteProjectDocument = async (
    fullPath: string,
    isFolder: boolean = false
  ): Promise<void> => {
    const itemName = fullPath.split(/[/\\]/).pop() || fullPath
    try {
      await deleteItemMutation.mutateAsync({
        projectId: project.id,
        owner: project.Created_by,
        path: fullPath,
        isFolder,
      })
      notify.toast.success(
        `${itemName} ${isFolder ? "folder" : "file"} deleted successfully`
      )
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      console.error(
        `Error deleting ${isFolder ? "folder" : "document"}:`,
        errorMessage
      )
      notify.toast.error(`Failed to delete ${itemName}`)
    }
  }

  /**
   * Downloads a document from the project.
   *
   * @param fullPath - The full path of the file to download.
   */
  const downloadProjectDocument = async (fullPath: string): Promise<void> => {
    // Extract filename from path for display
    const filename = fullPath.split(/[/\\]/).pop() || fullPath

    // Show loading message
    const hideLoading = notify.toast.loading(`Downloading ${filename}...`)

    try {
      await downloadMutation.mutateAsync({
        projectId: project.id,
        owner: project.Created_by,
        path: fullPath,
      })
      hideLoading()
      notify.toast.success(`${filename} downloaded successfully`)
    } catch (error) {
      hideLoading()
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      console.error("Error downloading document:", errorMessage)
      notify.toast.error(`Failed to download ${filename}`)
    }
  }

  const columns: ColumnsType<DocumentItem> = getColumns(
    deleteProjectDocument,
    downloadProjectDocument,
    onFolderClick
  )

  return (
    <Table<DocumentItem>
      loading={isLoading}
      size="small"
      className="w-full"
      rowClassName={(record) =>
        record.type === "folder" ? "cursor-pointer hover:bg-gray-50" : ""
      }
      rowKey={(record: DocumentItem) => record.fullPath}
      dataSource={documents}
      columns={columns}
      pagination={false}
      locale={{
        emptyText: "No documents found",
      }}
    />
  )
}

export default DocumentTable
